import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { cn, formatCurrency } from '@/lib/utils';
import type { Budget } from '@/types';

export type ProgressSegmentModalKind = 'children' | 'allocated';

interface BudgetProgressSegmentDialogProps {
  open: boolean;
  kind: ProgressSegmentModalKind | null;
  budget: Budget;
  onOpenChange: (open: boolean) => void;
  onOpenBudget: (budgetId: string) => void;
}

function childRemaining(child: Budget): number {
  return child.remaining ?? child.amount - (child.spent ?? 0);
}

function childSpent(child: Budget): number {
  return child.spent ?? 0;
}

export function BudgetProgressSegmentDialog({
  open,
  kind,
  budget,
  onOpenChange,
  onOpenBudget,
}: BudgetProgressSegmentDialogProps) {
  const currency = budget.currency;
  const allocated = budget.allocated ?? 0;
  const children = budget.children ?? [];

  const spendingChildren = React.useMemo(
    () =>
      children
        .filter((child) => childSpent(child) > 0)
        .sort((a, b) => childSpent(b) - childSpent(a)),
    [children]
  );

  const incompleteChildren = React.useMemo(
    () =>
      children
        .filter((child) => childRemaining(child) > 0)
        .sort((a, b) => childRemaining(b) - childRemaining(a)),
    [children]
  );

  const paidOnChildren = spendingChildren.reduce((sum, child) => sum + childSpent(child), 0);
  const remainingOnChildren = incompleteChildren.reduce(
    (sum, child) => sum + childRemaining(child),
    0
  );

  const paidRatio = allocated > 0 ? Math.min(100, (paidOnChildren / allocated) * 100) : 0;
  const remainingRatio =
    allocated > 0 ? Math.min(100, (remainingOnChildren / allocated) * 100) : 0;

  const isChildren = kind === 'children';
  const rows = isChildren ? spendingChildren : incompleteChildren;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isChildren ? 'Children spending' : 'Allocated unspent'}
          </DialogTitle>
          <DialogDescription>
            {isChildren
              ? 'Child budgets that already have spending, against total allocation.'
              : 'Child budgets that still have remaining allocation.'}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-2">
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-muted-foreground">Total allocated</span>
            <span className="font-semibold tabular-nums">
              {formatCurrency(allocated, currency)}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="text-muted-foreground">
              {isChildren ? 'Already paid' : 'Still remaining'}
            </span>
            <span className="font-semibold tabular-nums">
              {formatCurrency(isChildren ? paidOnChildren : remainingOnChildren, currency)}
            </span>
          </div>
          <div className="pt-1">
            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground mb-1">
              <span>{isChildren ? 'Paid of allocation' : 'Remaining of allocation'}</span>
              <span className="tabular-nums">
                {(isChildren ? paidRatio : remainingRatio).toFixed(0)}%
              </span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-background">
              <div
                className={cn(
                  'h-full rounded-full',
                  isChildren ? 'bg-progress-unallocated' : 'bg-progress-allocated'
                )}
                style={{
                  width: `${isChildren ? paidRatio : remainingRatio}%`,
                }}
              />
            </div>
          </div>
        </div>

        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            {isChildren
              ? 'No child budgets have spending yet.'
              : 'All allocated child budgets are fully spent.'}
          </p>
        ) : (
          <ul className="space-y-2">
            {rows.map((child) => {
              const spent = childSpent(child);
              const remaining = childRemaining(child);
              const ratio = child.amount > 0
                ? Math.min(100, ((isChildren ? spent : remaining) / child.amount) * 100)
                : 0;

              return (
                <li key={child.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onOpenChange(false);
                      onOpenBudget(child.id);
                    }}
                    className="w-full rounded-lg border border-border bg-card px-3 py-2.5 text-left hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-3 min-w-0">
                      <span className="font-medium text-foreground truncate">{child.name}</span>
                      <span className="shrink-0 text-sm font-semibold tabular-nums">
                        {formatCurrency(isChildren ? spent : remaining, child.currency)}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
                      <span>
                        of {formatCurrency(child.amount, child.currency)} allocated
                      </span>
                      <span className="tabular-nums">{ratio.toFixed(0)}%</span>
                    </div>
                    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className={cn(
                          'h-full rounded-full',
                          isChildren ? 'bg-progress-unallocated' : 'bg-progress-allocated'
                        )}
                        style={{ width: `${ratio}%` }}
                      />
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
